import { useState, useRef, useEffect } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { bankDeductions } from "@/mocks/systemModules";

type BankTab = "deduction" | "arrears" | "settlement" | "reconcile" | "thirdparty" | "aianalysis";

const statusConfig: Record<string, { color: string; bg: string }> = {
  "成功": { color: "text-green-600", bg: "bg-green-50" },
  "失败": { color: "text-red-600", bg: "bg-red-50" },
  "待扣款": { color: "text-amber-600", bg: "bg-amber-50" },
};

const arrearsData = [
  { id: "R001", name: "张建国", phone: "138****5678", amount: 245.60, months: 2, lastPay: "2026-02-01", bank: "工商银行", status: "待代扣" },
  { id: "R008", name: "李秀英", phone: "139****1234", amount: 189.30, months: 1, lastPay: "2026-03-01", bank: "建设银行", status: "代扣失败" },
  { id: "R015", name: "王大明", phone: "137****9876", amount: 567.80, months: 3, lastPay: "2026-01-01", bank: "农业银行", status: "待代扣" },
  { id: "R022", name: "赵小红", phone: "136****5432", amount: 123.40, months: 1, lastPay: "2026-03-15", bank: "招商银行", status: "待代扣" },
  { id: "R031", name: "陈志远", phone: "135****2468", amount: 345.20, months: 2, lastPay: "2026-02-15", bank: "工商银行", status: "代扣失败" },
];

const settlementData = [
  { id: "SET-2026-0401", date: "2026-04-01", bank: "工商银行", count: 1245, amount: 256780.50, status: "已结算", settleDate: "2026-04-03" },
  { id: "SET-2026-0301", date: "2026-03-01", bank: "建设银行", count: 892, amount: 184560.20, status: "已结算", settleDate: "2026-03-03" },
  { id: "SET-2026-0201", date: "2026-02-01", bank: "农业银行", count: 634, amount: 128940.80, status: "已结算", settleDate: "2026-02-03" },
  { id: "SET-2026-0101", date: "2026-01-01", bank: "招商银行", count: 456, amount: 98760.30, status: "已结算", settleDate: "2026-01-03" },
];

const thirdPartyPlatforms = [
  { name: "微信支付", icon: "ri-wechat-pay-line", color: "text-green-600", bg: "bg-green-50", status: "已接入", txCount: 8456, amount: 245680 },
  { name: "支付宝", icon: "ri-alipay-line", color: "text-sky-500", bg: "bg-sky-50", status: "已接入", txCount: 6234, amount: 189450 },
  { name: "云闪付", icon: "ri-bank-card-line", color: "text-red-500", bg: "bg-red-50", status: "已接入", txCount: 2156, amount: 68920 },
  { name: "工商银行网银", icon: "ri-bank-line", color: "text-gray-600", bg: "bg-gray-50", status: "已接入", txCount: 1245, amount: 256780 },
  { name: "建设银行网银", icon: "ri-bank-line", color: "text-gray-600", bg: "bg-gray-50", status: "已接入", txCount: 892, amount: 184560 },
  { name: "智能表管理系统", icon: "ri-cpu-line", color: "text-[#E8531A]", bg: "bg-orange-50", status: "已接入", txCount: 12456, amount: 0, note: "数据同步" },
];

const aiReportText = `【AI银行代扣分析报告 - 2026年4月】

一、代扣执行情况
本月共发起代扣 ${bankDeductions.length} 笔，成功 ${bankDeductions.filter(b => b.status === "成功").length} 笔，失败 ${bankDeductions.filter(b => b.status === "失败").length} 笔，代扣成功率 ${((bankDeductions.filter(b => b.status === "成功").length / bankDeductions.length) * 100).toFixed(1)}%。

二、失败原因分析
AI分析代扣失败主要原因：
• 余额不足：占失败笔数 45.2%
• 账户状态异常：占失败笔数 28.6%
• 银行系统超时：占失败笔数 18.3%
• 其他原因：占失败笔数 7.9%

三、欠费风险预警
当前欠费用户 5 名，总欠费金额 ¥1,471.30。AI模型预测：
• 高风险用户（欠费3个月以上）：2名，建议暂停供气
• 中风险用户（欠费1-2个月）：3名，建议短信催缴+代扣重试

四、优化建议
1. 对代扣失败用户在3个工作日内自动重试
2. 建议与更多银行建立代扣协议，提升覆盖率
3. 推广微信/支付宝自动扣款，降低代扣失败率
4. 对欠费超过90天的用户启动法律催收程序`;

export default function BankPage() {
  const [activeTab, setActiveTab] = useState<BankTab>("deduction");
  const [filter, setFilter] = useState("全部");
  const [batchModal, setBatchModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const [running, setRunning] = useState(false);
  const [aiReport, setAiReport] = useState("");
  const [generating, setGenerating] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const statuses = ["全部", "成功", "失败", "待扣款"];
  const filtered = filter === "全部" ? bankDeductions : bankDeductions.filter(b => b.status === filter);

  const stats = {
    total: bankDeductions.length,
    success: bankDeductions.filter(b => b.status === "成功").length,
    failed: bankDeductions.filter(b => b.status === "失败").length,
    pending: bankDeductions.filter(b => b.status === "待扣款").length,
    totalAmount: bankDeductions.filter(b => b.status === "成功").reduce((s, b) => s + b.amount, 0),
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || activeTab !== "aianalysis") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const bankData = [
      { name: "工商银行", success: 1245, failed: 23, amount: 256780 },
      { name: "建设银行", success: 892, failed: 18, amount: 184560 },
      { name: "农业银行", success: 634, failed: 12, amount: 128940 },
      { name: "招商银行", success: 456, failed: 8, amount: 98760 },
      { name: "中国银行", success: 312, failed: 5, amount: 68920 },
    ];
    const maxV = Math.max(...bankData.map(d => d.success + d.failed));
    const barW = (w - 60) / bankData.length;

    bankData.forEach((d, i) => {
      const x = 30 + i * barW;
      const bw = barW * 0.65;
      const totalH = (((d.success + d.failed) / maxV) * (h - 50));
      const successH = ((d.success / (d.success + d.failed)) * totalH);
      const failH = totalH - successH;

      const grad = ctx.createLinearGradient(x, h - 30 - totalH, x, h - 30);
      grad.addColorStop(0, "rgba(34,197,94,0.9)");
      grad.addColorStop(1, "rgba(34,197,94,0.3)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x + bw * 0.1, h - 30 - totalH, bw * 0.8, successH, [3, 3, 0, 0]);
      ctx.fill();

      ctx.fillStyle = "rgba(239,68,68,0.7)";
      ctx.beginPath();
      ctx.roundRect(x + bw * 0.1, h - 30 - failH, bw * 0.8, failH, [0, 0, 3, 3]);
      ctx.fill();

      ctx.fillStyle = "rgba(80,80,80,0.7)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(d.name, x + bw / 2, h - 12);
      ctx.fillStyle = "#22c55e";
      ctx.font = "bold 9px sans-serif";
      ctx.fillText(`${d.success}`, x + bw / 2, h - 30 - totalH - 4);
    });
  }, [activeTab]);

  const generateReport = () => {
    setGenerating(true);
    setAiReport("");
    const text = customPrompt
      ? `【自定义AI分析报告】\n\n根据您的要求「${customPrompt}」，AI分析如下：\n\n${aiReportText}`
      : aiReportText;
    let i = 0;
    const timer = setInterval(() => {
      if (i >= text.length) { clearInterval(timer); setGenerating(false); return; }
      setAiReport(text.slice(0, i + 4));
      i += 4;
    }, 25);
  };

  const startBatch = () => {
    setRunning(true);
    setProgress(0);
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) { clearInterval(timer); setRunning(false); return 100; }
        return prev + 5;
      });
    }, 150);
  };

  const tabs = [
    { key: "deduction", label: "代扣记录", icon: "ri-bank-card-line" },
    { key: "arrears", label: "欠费查询", icon: "ri-error-warning-line" },
    { key: "settlement", label: "结算记录", icon: "ri-file-list-3-line" },
    { key: "reconcile", label: "对账管理", icon: "ri-scales-line" },
    { key: "thirdparty", label: "第三方对接", icon: "ri-links-line" },
    { key: "aianalysis", label: "AI分析报告", icon: "ri-brain-line" },
  ];

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">银行代扣系统</h1>
            <p className="text-sm text-gray-500 mt-0.5">欠费查询 · 银行代扣 · 结算对账 · AI分析 · 第三方对接</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setBatchModal(true)} className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#d44a15] cursor-pointer whitespace-nowrap">
              <i className="ri-bank-card-line"></i>发起批量代扣
            </button>
            <button className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line"></i>导出对账单
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: "代扣总笔数", value: stats.total, color: "text-gray-800", bg: "bg-gray-50", icon: "ri-list-check" },
            { label: "成功", value: stats.success, color: "text-green-600", bg: "bg-green-50", icon: "ri-checkbox-circle-line" },
            { label: "失败", value: stats.failed, color: "text-red-600", bg: "bg-red-50", icon: "ri-close-circle-line" },
            { label: "待扣款", value: stats.pending, color: "text-amber-600", bg: "bg-amber-50", icon: "ri-time-line" },
            { label: "成功金额", value: `¥${stats.totalAmount.toLocaleString()}`, color: "text-[#E8531A]", bg: "bg-orange-50", icon: "ri-money-cny-circle-line" },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
              <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-white">
                <i className={`${s.icon} ${s.color} text-base`}></i>
              </div>
              <div>
                <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit flex-wrap">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key as BankTab)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <i className={t.icon}></i>{t.label}
            </button>
          ))}
        </div>

        {activeTab === "deduction" && (
          <>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              {statuses.map(s => (
                <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filter === s ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>{s}</button>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {["代扣编号", "用户姓名", "银行", "银行卡号", "代扣金额", "账期", "扣款日期", "状态", "操作"].map(h => (
                      <th key={h} className="text-left text-xs font-medium text-gray-500 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map(item => {
                    const cfg = statusConfig[item.status];
                    return (
                      <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.id}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.userName}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{item.bank}</td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-600">{item.cardNo}</td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-800">¥{item.amount.toFixed(2)}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{item.period}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{item.deductDate || "-"}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>{item.status}</span>
                          {item.failReason && <div className="text-[10px] text-red-400 mt-0.5">{item.failReason}</div>}
                        </td>
                        <td className="px-4 py-3">
                          {item.status === "失败" && <button className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap">重新代扣</button>}
                          {item.status === "待扣款" && <button className="text-xs text-amber-600 hover:underline cursor-pointer whitespace-nowrap">立即扣款</button>}
                          {item.status === "成功" && <button className="text-xs text-gray-400 hover:underline cursor-pointer whitespace-nowrap">查看凭证</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === "arrears" && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {["用户编号", "姓名", "手机号", "欠费金额", "欠费月数", "最后缴费", "签约银行", "状态", "操作"].map(h => (
                    <th key={h} className="text-left text-xs font-medium text-gray-500 px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {arrearsData.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.id}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.name}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{item.phone}</td>
                    <td className="px-4 py-3 text-sm font-bold text-red-600">¥{item.amount.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.months >= 3 ? "bg-red-50 text-red-600" : item.months >= 2 ? "bg-amber-50 text-amber-600" : "bg-gray-50 text-gray-600"}`}>{item.months}个月</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{item.lastPay}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{item.bank}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.status === "代扣失败" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>{item.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap">立即代扣</button>
                        <button className="text-xs text-gray-400 hover:underline cursor-pointer whitespace-nowrap">发短信</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "settlement" && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {["结算编号", "结算日期", "银行", "代扣笔数", "结算金额", "状态", "到账日期", "操作"].map(h => (
                    <th key={h} className="text-left text-xs font-medium text-gray-500 px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {settlementData.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.bank}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.count.toLocaleString()} 笔</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#E8531A]">¥{item.amount.toLocaleString()}</td>
                    <td className="px-4 py-3"><span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">{item.status}</span></td>
                    <td className="px-4 py-3 text-xs text-gray-600">{item.settleDate}</td>
                    <td className="px-4 py-3">
                      <button className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap">查看明细</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "reconcile" && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "系统应收", value: "¥856,420.50", icon: "ri-file-list-3-line", color: "text-gray-800", bg: "bg-gray-50" },
                { label: "银行实收", value: "¥852,180.30", icon: "ri-bank-line", color: "text-green-600", bg: "bg-green-50" },
                { label: "差异金额", value: "¥4,240.20", icon: "ri-scales-line", color: "text-amber-600", bg: "bg-amber-50" },
              ].map((s, i) => (
                <div key={i} className={`${s.bg} rounded-xl p-5`}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className={`${s.icon} ${s.color} text-lg`}></i>
                    <span className="text-xs text-gray-500">{s.label}</span>
                  </div>
                  <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">对账差异明细</h3>
              {[
                { id: "D001", type: "多收", amount: 120.50, reason: "重复代扣", user: "张建国", date: "2026-04-05", aiReason: "系统检测到同一账期重复发起代扣" },
                { id: "D002", type: "少收", amount: 89.30, reason: "退款未到账", user: "李秀英", date: "2026-04-06", aiReason: "用户申请退款，银行处理中" },
                { id: "D003", type: "差异", amount: 4030.40, reason: "手续费差异", user: "批量代扣", date: "2026-04-01", aiReason: "银行手续费率调整导致差异" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.type === "多收" ? "bg-red-50 text-red-600" : item.type === "少收" ? "bg-amber-50 text-amber-600" : "bg-gray-50 text-gray-600"}`}>{item.type}</span>
                    <div>
                      <div className="text-sm font-medium text-gray-800">{item.reason} - {item.user}</div>
                      <div className="text-xs text-gray-400">{item.date} · AI分析: {item.aiReason}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-800">¥{item.amount.toFixed(2)}</div>
                    <button className="text-xs text-[#E8531A] cursor-pointer">处理</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "thirdparty" && (
          <div className="space-y-4">
            <div className="bg-orange-50 rounded-xl p-4 flex items-start gap-3">
              <i className="ri-information-line text-[#E8531A] text-lg mt-0.5"></i>
              <div>
                <div className="text-sm font-semibold text-[#E8531A] mb-1">第三方代收费系统对接说明</div>
                <p className="text-xs text-gray-600 leading-relaxed">本系统支持与微信支付、支付宝、银联云闪付、各大银行网银及智能表管理系统对接，实现多渠道缴费数据自动汇总与对账。</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {thirdPartyPlatforms.map((platform, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl ${platform.bg} flex items-center justify-center`}>
                      <i className={`${platform.icon} ${platform.color} text-xl`}></i>
                    </div>
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">{platform.status}</span>
                  </div>
                  <div className="text-sm font-semibold text-gray-800 mb-2">{platform.name}</div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">{platform.note || "交易笔数"}</span>
                      <span className="font-medium text-gray-700">{platform.txCount.toLocaleString()}</span>
                    </div>
                    {platform.amount > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">交易金额</span>
                        <span className="font-medium text-[#E8531A]">¥{platform.amount.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                  <button className="w-full mt-3 border border-gray-200 text-gray-600 py-1.5 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">配置</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "aianalysis" && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-bar-chart-grouped-line text-[#E8531A]"></i>
                <span className="text-sm font-semibold text-gray-800">各银行代扣成功率分析</span>
              </div>
              <canvas ref={canvasRef} width={800} height={200} className="w-full" />
              <div className="mt-3 flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-green-400"></div><span className="text-gray-500">成功笔数</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-400"></div><span className="text-gray-500">失败笔数</span></div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <i className="ri-robot-2-line text-[#E8531A]"></i>
                  <span className="text-sm font-semibold text-gray-800">AI打字式分析报告</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    value={customPrompt}
                    onChange={e => setCustomPrompt(e.target.value)}
                    placeholder="自定义分析要求（可选）..."
                    className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 w-52 focus:outline-none focus:border-[#E8531A]"
                  />
                  <button
                    onClick={generateReport}
                    disabled={generating}
                    className="flex items-center gap-1.5 text-xs bg-[#E8531A] text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap"
                  >
                    <i className={`${generating ? "ri-loader-4-line animate-spin" : "ri-magic-line"}`}></i>
                    {generating ? "生成中..." : "生成报告"}
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 min-h-48 max-h-72 overflow-y-auto">
                {aiReport ? (
                  <pre className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">{aiReport}{generating && <span className="animate-pulse text-[#E8531A]">|</span>}</pre>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-center">
                    <i className="ri-robot-2-line text-gray-300 text-3xl mb-2"></i>
                    <p className="text-xs text-gray-400">点击「生成报告」，AI将自动分析代扣数据</p>
                    <p className="text-xs text-gray-400 mt-1">也可在输入框中填写自定义分析要求</p>
                  </div>
                )}
              </div>
              {aiReport && !generating && (
                <div className="flex gap-2 mt-3">
                  <button className="flex-1 border border-[#E8531A] text-[#E8531A] py-2 rounded-lg text-xs cursor-pointer hover:bg-orange-50 whitespace-nowrap">
                    <i className="ri-download-line mr-1"></i>下载PDF
                  </button>
                  <button onClick={() => setAiReport("")} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                    清除重新生成
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {batchModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !running && setBatchModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[440px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">发起批量代扣</h3>
            {!running && progress === 0 ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">代扣账期</label>
                  <input type="month" defaultValue="2026-04" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">代扣范围</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
                    <option>全部已签约用户</option>
                    <option>欠费用户</option>
                    <option>指定用户</option>
                  </select>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-700">
                  <i className="ri-information-line mr-1"></i>
                  本次将对 {stats.pending + stats.failed} 名用户发起代扣
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setBatchModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
                  <button onClick={startBatch} className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">确认发起</button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="text-sm text-gray-600 mb-3">{progress < 100 ? "正在执行批量代扣..." : "代扣执行完成！"}</div>
                <div className="w-full bg-gray-100 rounded-full h-3 mb-2">
                  <div className="bg-[#E8531A] h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
                <div className="text-xs text-gray-400 mb-4">{progress}%</div>
                {progress === 100 && (
                  <button onClick={() => { setBatchModal(false); setProgress(0); }} className="bg-green-500 text-white px-6 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap">完成</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </MainLayout>
  );
}
